<!--
  A review thread's Local agent panel, shown under the thread's actions
  while the thread's Local agent button is on: the agents whose grant on
  the pull request covers what a thread carries (api/relayTask.ts
  THREAD_NEEDS: its comments, and the file and lines it is on), sending the
  thread to one, and its results; and why another agent with a grant here
  isn't offered. It follows the tab's grants (state/relayAgents.svelte.ts)
  while open, and polls the chosen agent until it is destroyed or no
  longer offered. What was sent to an agent no longer offered (its grant
  narrowed, refused or revoked since) stays, with Cancel, polled while it
  is queued or working: only new requests go by the grant. Asking an agent
  about the thread tells it what the person is looking at, so the panel
  asks only the agents granted the pull request and those this browser
  sent the thread to (relay.sentTo, kept across tabs and reloads): a
  request sent before a grant was revoked is found again, and no other
  agent hears of the thread.

  The agent's result is Markdown, rendered as a comment's is (Html, which
  sanitizes it). Once the chosen agent has a request on the thread, the
  request form goes: the conversation continues in its reply box. A
  completed answer can become the person's own draft reply on the GitHub
  thread (Draft as my reply), offered only where they can reply and the
  answer is about the head they are looking at.

  It is compact: one status line (the agent, a dot for its presence, a
  short word, and how to get its attention when it isn't listening), the
  results, then Send with the request folded behind Edit request. The
  thread's own Send to <agent> (Thread) opens it and sends at once
  (`sendNow`), unless that agent already has a request here.

  The requests and answers read as a conversation, oldest first, each turn
  laid out as the thread's own comments are (an author line, then the
  body): what the person asked (relay.ts `asked`), under their login and
  avatar, then the agent's answer, under its name and how the request
  stands. Only the newest exchange shows at first: the earlier ones fold
  into one line (how many, and how the last of them stood), opened and
  closed per thread for the rest of the tab (`unfolded`). The thread's
  own <Agent>'s answer toggle opens and closes the whole panel. Under it, a reply box: a reply is a new task on the
  thread's topic, with the thread's task as usual (logic/threadTask: a
  range task on the thread's lines, with its comments), the reply as its
  instruction, and the turns before it as `conversation`
  (logic/relayConversation, kept under the helper's size limit).

  The same panel serves a question about the whole pull request (`about`,
  logic/prAsk): its own topic and task (a subject task, which needs
  metadata granted), no request form (the conversation's composer asks,
  `askNow`), and Draft as my reply makes the answer a pull request comment
  draft for the review.
-->
<script lang="ts" module>
  import type { RelayCategory } from '../api/relaySubject';
  import type { TaskBody } from '../api/relayTask';
  import type { ReviewThread } from '../api/queries';
  import type { Conversation } from '../state/rangeHost';
  /**
   * What the panel is about, when not a review thread: the task topic,
   * what a request carries (`task`, with the passage asked about if any)
   * and so what its grant must cover (`needs`, said as `carries`), whether
   * and how an answer becomes the person's draft, the word for what is
   * sent ("thread", "question"), and whether the panel offers its own
   * request form (a thread's) or another box asks (`askNow`).
   */
  export interface RelayAbout {
    topic: string;
    task: (instruction: string, quote?: string | null) => TaskBody | Promise<TaskBody>;
    needs: readonly RelayCategory[];
    carries: string;
    canDraft: boolean;
    draft: (text: string, key: string) => Promise<boolean>;
    draftTitle: string;
    noun: string;
    asksHere: boolean;
    /** What a request says unless the person edits it (a thread's: THREAD_REQUEST). */
    request?: string;
  }
  /**
   * Where the subject's local agents are managed: another page, linked
   * (`path`, called `place`: a pull request's Overview), or the page the
   * panel is on (`here`: a compare's header, its Local agents button).
   */
  export type AgentsAt = { at: 'link'; path: string; place: string } | { at: 'here'; where: string };
  /**
   * The page the panel is on, as it reads it: the subject asked about, its
   * head (an answer about another one becomes no draft), the viewer, where
   * the subject's local agents are managed, what it is called, and whether
   * drafts can be made. A pull request's comes from its conversation; a
   * compare's page gives its own.
   */
  export interface RelayPage {
    subject: import('../api/relaySubject').SubjectKey;
    head: string | null;
    viewer: string;
    agents: AgentsAt;
    noun: string;
    draftsOff: boolean;
    draftsOffText: string | null;
  }
  /**
   * What the panel is on, one of three: a pull request's review thread; a
   * question about a pull request (its conversation, `about`); or a page
   * without a conversation (a compare) asking about `about`.
   */
  export type RelayThreadProps =
    | { pr: Conversation; thread: ReviewThread; about?: undefined; page?: undefined }
    | { pr: Conversation; about: RelayAbout; thread?: undefined; page?: undefined }
    | { page: RelayPage; about: RelayAbout; pr?: undefined; thread?: undefined };
  /** The threads (their topics) whose earlier exchanges the person opened, in this tab. */
  const unfolded = new Set<string>();
</script>
<script lang="ts">
  import Html from './Html.svelte';
  import { prPath } from '../router';
  import { onDestroy, onMount, untrack } from 'svelte';
  import { relay, type RelayTask, type RelayPresence } from '../api/relay';
  import { pullKey } from '../api/relaySubject';
  import { lacking, THREAD_NEEDS, threadTopic } from '../api/relayTask';
  import { scopeText } from '../logic/relayText';
  import { turnsOf, withTurns } from '../logic/relayConversation';
  import { THREAD_REQUEST, threadTask } from '../logic/threadTask';
  import { relayAgents } from '../state/relayAgents.svelte';
  import { session } from '../state/session.svelte';
  const props: RelayThreadProps = $props();
  const pr = $derived(props.pr ?? null);
  const thread = $derived(props.thread ?? null);
  const about = $derived(props.about ?? null);
  /** A pull request's page, from its conversation. */
  const pullPage = (c: Conversation): RelayPage => ({
    subject: pullKey(c.owner, c.repo, c.number),
    head: c.headRefOid,
    viewer: c.viewer,
    agents: { at: 'link', path: prPath(c.owner, c.repo, c.number), place: 'Overview' },
    noun: 'pull request',
    draftsOff: c.draftsOff,
    draftsOffText: c.draftsOffText,
  });
  const page: RelayPage = $derived(props.page ?? pullPage(props.pr!));
  /** What it is about: `about`, else the review thread. */
  const ab: RelayAbout = $derived(
    about ?? {
      topic: threadTopic(page.subject, thread!.id),
      task: (i: string) => threadTask(pr!, thread!, i),
      needs: THREAD_NEEDS,
      carries: 'its comments, and the file and lines it is on',
      canDraft: thread!.viewerCanReply,
      draft: (text: string, key: string) => pr!.reply(thread!.id, text, { key, text }),
      draftTitle: 'Start your reply on this GitHub thread with this answer, to edit and publish with your review',
      noun: 'thread',
      asksHere: true,
    },
  );
  const ref = $derived(page.subject);
  /** The paired agents with a grant here, each with it. */
  const onPr = $derived(relayAgents.grantsOn(ref).flatMap((g) => {
    const c = relayAgents.connection(g.connection);
    return c ? [{ g, c }] : [];
  }));
  /** Those it can be sent to: the grant in force and covering what is sent. */
  const connections = $derived(relayAgents.taskAgents(ref, ab.needs));
  /** Those whose grant lacks some of what is sent, with what. */
  const narrow = $derived(onPr.flatMap(({ g, c }) => { const lacks = lacking(ab.needs, g.scope); return g.status === 'granted' && lacks.length ? [{ c, lacks }] : []; }));
  /** Those whose grant their computer refused. */
  const refused = $derived(onPr.flatMap(({ g, c }) => g.status === 'refused' ? [{ c, reason: g.reason }] : []));
  let selected = $state('');
  /** Each agent's tasks on this thread, as last read. */
  let tasksBy = $state<Record<string, RelayTask[]>>({});
  const tasks = $derived(tasksBy[selected] ?? []);
  /** The agents this browser sent the thread to, as recorded (null until read). */
  let sentTo = $state<string[] | null>(null);
  /** Whether `id` may be asked about this thread: granted the pull request, or sent the thread. */
  const askable = (id: string) => onPr.some((x) => x.c.id === id) || !!sentTo?.includes(id);
  /** Paired agents not offered, that may be asked, whose requests here are still to show: not read yet (to find them), or with tasks. */
  const held = $derived(relayAgents.connections.filter((c) => !connections.some((x) => x.id === c.id) && askable(c.id) && (!(c.id in tasksBy) || !!tasksBy[c.id].length)));
  /** A held agent's task that can still change: one unconfirmed waits for a retry, which a held agent isn't given. */
  const moving = (t: RelayTask) => t.status === 'queued' || t.status === 'working';
  let error = $state('');
  let connectionError = $state('');
  let busy = $state(false);
  let presence = $state<RelayPresence>('offline');
  let reachable = $state(false);
  // svelte-ignore state_referenced_locally
  let instruction = $state(about?.request ?? THREAD_REQUEST);
  /** The request editor is open (Edit request). */
  let editing = $state(false);
  /** Whether a request to the chosen agent here is under way, so another can't be sent. */
  const underway = $derived(tasks.some(t => ['queued','working','unconfirmed'].includes(t.status)));
  /** The request form shows: nothing sent to the chosen agent here yet (after, the reply box continues it). */
  const asking = $derived(!tasks.length);
  /** Bumped to stop every poll started before it (`repoll`, destroy). */
  let generation = 0;
  let destroyed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const topic = $derived(ab.topic);
  /** Whether this thread's earlier exchanges show (`unfolded`). */
  // svelte-ignore state_referenced_locally (a thread's topic is fixed for its life)
  let earlier = $state(unfolded.has(props.about?.topic ?? threadTopic(pullKey(props.pr!.owner, props.pr!.repo, props.pr!.number), props.thread!.id)));
  function toggleEarlier() {
    earlier = !earlier;
    if (earlier) unfolded.add(topic); else unfolded.delete(topic);
  }
  /** Who the person's turns are by, as the thread's comments name them. */
  const me = $derived(page.viewer || session.user?.login || 'You');
  /** Where a request stands, in a word or two. */
  const stateText = (task: RelayTask) => task.status === 'working' && task.lease_until && task.lease_until < Date.now() ? 'Disconnected · awaiting retry' : task.status === 'queued' ? 'Delivered · waiting for agent' : task.status === 'completed' ? 'Ready for review' : task.status === 'working' ? 'Working' : task.status === 'failed' ? 'Needs attention' : task.status === 'cancelled' ? 'Cancelled' : 'Delivery unconfirmed';
  const why = (e: unknown) => (e instanceof Error ? e.message : String(e));
  /** Whether a completed answer can be the person's draft reply: they can reply here, and it answers the head they see. */
  const draftable = (task: RelayTask) => task.status === 'completed' && ab.canDraft && task.payload.head === page.head;
  async function refresh(epoch: number) {
    const id = selected;
    // Down, a poll would only fail (relay.workerDown): asked again once it is back.
    if (!id || relayAgents.workerDown) return;
    try {
      const result = await relay.tasks(id, topic);
      if (epoch === generation && id === selected) {
        tasksBy[id] = result.tasks.filter(t => t.topic === topic);
        presence = result.presence ?? (result.online ? 'connected' : 'offline'); reachable = result.reachable ?? true; connectionError = result.error ?? '';
      }
    } catch(e) { if (epoch === generation) connectionError = why(e); }
    // Often while a request is under way; slowly otherwise, for the presence line (a sent thread's panel opens by itself).
    if (epoch === generation) timer = setTimeout(() => { void refresh(epoch); }, tasksBy[id]?.some((t) => moving(t) || t.status === 'unconfirmed') ? 5000 : 30_000);
  }
  /** Poll again now, as the only poll: one in flight or waiting stops when it returns. */
  function repoll() { if (destroyed) return; generation++; clearTimeout(timer); void refresh(generation); }
  function choose() { if (selected) tasksBy[selected] = []; error = ''; connectionError = ''; repoll(); }
  // The chosen agent, while it is offered: one revoked, narrowed or refused
  // meanwhile gives way to the first still offered (its poll stops), and
  // the first one granted is chosen.
  $effect(() => {
    const ids = connections.map((c) => c.id);
    untrack(() => { if (!ids.includes(selected)) { selected = ids[0] ?? ''; choose(); } });
  });
  // Whom this browser sent the thread to, from any tab, before now.
  onMount(() => void relay.sentTo(topic).then((ids) => { if (!destroyed) sentTo = [...new Set([...(sentTo ?? []), ...ids])]; }, () => { if (!destroyed) sentTo ??= []; }));
  /** The polls of the agents held, each stopped when it is no longer held. */
  const holds = new Map<string, () => void>();
  /** Reads `id`'s tasks here now, and again while one is in progress. Returns the stop. */
  function watch(id: string): () => void {
    let stopped = false;
    let wait: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (relayAgents.workerDown) return;
      try {
        const result = await relay.tasks(id, topic);
        if (!stopped) tasksBy[id] = result.tasks.filter((t) => t.topic === topic);
      } catch {
        // Shown as last read; asked again while in progress.
      }
      if (!stopped && tasksBy[id]?.some(moving)) wait = setTimeout(() => void poll(), 5000);
    };
    void poll();
    return () => { stopped = true; clearTimeout(wait); };
  }
  $effect(() => {
    // None while the connection is down; each again, read at once, when it is back.
    const ids = relayAgents.workerDown ? [] : held.map((c) => c.id);
    untrack(() => {
      for (const [id, stop] of holds) if (!ids.includes(id)) { stop(); holds.delete(id); }
      if (!destroyed) for (const id of ids) if (!holds.has(id)) holds.set(id, watch(id));
    });
  });
  let wasDown = false;
  $effect(() => {
    const down = !!relayAgents.workerDown;
    untrack(() => { if (wasDown && !down) repoll(); wasDown = down; });
  });
  onDestroy(() => { destroyed = true; generation++; clearTimeout(timer); for (const stop of holds.values()) stop(); holds.clear(); });
  async function send() {
    // Only to an agent offered now, whatever the select still says.
    if (busy || !connections.some((c) => c.id === selected)) return;
    busy = true; error = '';
    try {
      const to = selected;
      // Recorded by relay.send before it delivers (a delivery that fails may still have reached it).
      sentTo = [...new Set([...(sentTo ?? []), to])];
      await relayAgents.sendTask(to, topic, await ab.task(instruction));
      editing = false;
      choose();
    } catch(e) { error = why(e); repoll(); } finally { busy = false; }
  }
  /** The reply being written to the chosen agent, under the conversation. */
  let replyText = $state('');
  /** Sends `replyText` as the next turn: a new task on this thread with the turns before it. */
  async function reply() {
    const text = replyText.trim();
    if (!text || busy || underway || !connections.some((c) => c.id === selected)) return;
    busy = true; error = '';
    try {
      const to = selected;
      sentTo = [...new Set([...(sentTo ?? []), to])];
      await relayAgents.sendTask(to, topic, withTurns(await ab.task(text), turnsOf(tasks)));
      replyText = '';
    } catch(e) { error = why(e); } finally { busy = false; repoll(); }
  }
  /** Ctrl/Cmd+Enter in the reply box sends it (never mid-composition). */
  function replyKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.isComposing) { e.preventDefault(); void reply(); }
  }
  /**
   * The thread's Send to <agent> (Thread): chooses `agent` and sends the
   * thread with the request as it stands, unless that agent already has a
   * request here (read first), whose results are shown instead.
   */
  export async function sendNow(agent: string): Promise<void> {
    if (busy || !connections.some((c) => c.id === agent)) return;
    if (selected !== agent) { selected = agent; choose(); }
    busy = true;
    let had = true;
    try { had = (await relay.tasks(agent, topic)).tasks.some((t) => t.topic === topic); } catch { had = false; } finally { busy = false; }
    if (!had && !destroyed && selected === agent) await send();
  }
  /**
   * Asks `agent` `text` from another box (a question about the pull
   * request): chooses it and sends `text` as a new request, with `quote`
   * (a passage of the description it is about) if any, after the turns
   * before it when there are any. Throws when it can't: that agent isn't
   * offered, or a request to it here is under way.
   */
  export async function askNow(agent: string, text: string, quote: string | null = null): Promise<void> {
    if (!connections.some((c) => c.id === agent)) throw new Error(`That agent can’t be asked about this ${page.noun}.`);
    if (selected !== agent) { selected = agent; choose(); }
    const known = (await relay.tasks(agent, topic)).tasks.filter((t) => t.topic === topic);
    tasksBy[agent] = known;
    if (known.some((t) => ['queued', 'working', 'unconfirmed'].includes(t.status))) throw new Error('A question to this agent is still under way. Wait for its answer, or cancel it.');
    busy = true; error = '';
    try {
      sentTo = [...new Set([...(sentTo ?? []), agent])];
      const body = await ab.task(text, quote);
      await relayAgents.sendTask(agent, topic, known.length ? withTurns(body, turnsOf(known)) : body);
    } finally { busy = false; repoll(); }
  }
  async function retry(task: RelayTask) {
    busy = true; error = '';
    try { await relay.retry(selected, task); choose(); } catch(e) { error = why(e); } finally { busy = false; }
  }
  /** Cancels `task` of `agent`'s, the chosen one or one held. */
  async function cancel(agent: string, task: RelayTask) {
    busy = true; error = '';
    try {
      await relay.cancel(agent, task.id);
      if (agent === selected) choose();
      else { holds.get(agent)?.(); holds.set(agent, watch(agent)); }
    } catch(e) { error = why(e); } finally { busy = false; }
  }
  async function draft(task: RelayTask) {
    if (!task.result || !draftable(task)) return;
    busy = true; error = '';
    try { if (!(await ab.draft(task.result, `relay:${task.id}`))) error = 'The reply draft could not be saved. Try again.'; } catch(e) { error = why(e); } finally { busy = false; }
  }
</script>
{#snippet taskView(task: RelayTask, agent: string, offered: boolean, name: string)}
  <div class="comment-head"><span class="agent-mark" aria-hidden="true"></span><b class="who">{name}</b><span class="task-state" class:alert={task.status === 'failed'}>{stateText(task)}</span></div>
  <div class="relay-result">
    {#if task.status === 'unconfirmed' && offered}<p>Delivery has not been confirmed. Retry to check without sending a duplicate.</p><div><button class="btn small" disabled={busy} onclick={() => retry(task)}>Retry delivery</button></div>
    {:else if task.status === 'unconfirmed'}<p>Its delivery wasn’t confirmed, and it can’t be retried: this agent isn’t offered for new requests.</p>{/if}
    {#if task.payload.head && task.payload.head !== page.head}<p>The {page.noun}’s head changed since this task was sent.</p>{/if}
    {#if task.result}<Html md={task.result} class="comment-body relay-body" />{#if draftable(task)}<div><button class="btn small" disabled={busy || page.draftsOff} title={page.draftsOffText ?? ab.draftTitle} onclick={() => draft(task)}>Draft as my reply</button></div>{/if}{/if}
    {#if task.status === 'queued' || task.status === 'working'}<div><button class="btn small" disabled={busy} onclick={() => cancel(agent, task)}>Cancel request</button></div>{/if}
  </div>
{/snippet}
<div class="relay-thread">
  {#if !relayAgents.loaded}<p class="muted">Loading your agents…</p>
  {:else if !onPr.length}<p class="relay-empty">No agent can read this {page.noun} yet. {#if page.agents.at === 'link'}On its <a href={page.agents.path}>{page.agents.place}</a>, choose <b>Connect my agent</b> under Local agents{:else}Open <b>Local agents</b> {page.agents.where} and choose <b>Connect my agent</b>{/if}, then send this {ab.noun}.</p>
  {:else}
    {#each refused as { c, reason } (c.id)}<p class="relay-empty relay-refused">{c.name}’s computer refused its grant on this {page.noun}: {reason.replace(/[.\s]+$/, '')}. Choose <b>Grant again</b> for it {#if page.agents.at === 'link'}on the {page.noun}’s <a href={page.agents.path}>{page.agents.place}</a>, under Local agents{:else}under <b>Local agents</b> {page.agents.where}{/if}.</p>{/each}
    {#each narrow as { c, lacks } (c.id)}<p class="relay-narrow">{c.name} can’t be sent {ab.noun}s: its grant here doesn’t include {scopeText(lacks)}, which a {ab.noun} carries ({ab.carries}). Add them {#if page.agents.at === 'link'}on the {page.noun}’s <a href={page.agents.path}>{page.agents.place}</a>, under Local agents{:else}under <b>Local agents</b> {page.agents.where}{/if}.</p>{/each}
  {/if}
  {#if connections.length}
    {@const status = !reachable ? { tone: 'off', text: 'Computer unreachable · showing last known status' } : presence === 'listening' ? { tone: 'live', text: 'Agent listening · encrypted end to end' } : presence === 'wakeable' ? { tone: 'live', text: 'Automatic agent notifications available' } : presence === 'channel' ? { tone: 'idle', text: 'Channel requested · awaiting agent acknowledgement' } : presence === 'connected' ? { tone: 'idle', text: 'Plugin connected · agent is not listening yet' } : { tone: 'off', text: 'Agent session offline · accepted requests wait on your computer' }}
    <div class="relay-status">
      {#if connections.length > 1}
        <select class="agent" aria-label="Agent" disabled={busy} bind:value={selected} onchange={choose}>{#each connections as c (c.id)}<option value={c.id}>{c.name}</option>{/each}</select>
      {:else}
        <select class="agent single" aria-label="Agent" disabled bind:value={selected}>{#each connections as c (c.id)}<option value={c.id}>{c.name}</option>{/each}</select>
      {/if}
      <span class="dot {status.tone}" aria-hidden="true"></span>
      <span role="status" class="status-text">{status.text}</span>
      {#if reachable && (presence === 'connected' || presence === 'offline' || presence === 'channel')}<details class="hint"><summary>Get its attention</summary><p>In the connected agent session, say: “Use the Changeling review skill and stay available for assigned review work.” A plugin connection alone cannot prove the agent is listening.</p></details>{/if}
    </div>
    {#if tasks.length}
      {@const name = connections.find((c) => c.id === selected)?.name ?? 'the agent'}
      {@const folded = earlier ? 0 : tasks.length - 1}
      {#if tasks.length > 1}
        <button class="fold-earlier" aria-expanded={earlier} onclick={toggleEarlier}>
          <span aria-hidden="true">{earlier ? '▾' : '▸'}</span>
          {earlier ? `Hide ${tasks.length - 1} earlier ${tasks.length === 2 ? 'request' : 'requests'}` : `${tasks.length - 1} earlier ${tasks.length === 2 ? 'request' : 'requests'} · last ${stateText(tasks[tasks.length - 2]).toLowerCase()}`}
        </button>
      {/if}
      <ol class="convo" aria-label="Conversation with {name}">
        {#each tasks.slice(folded) as task (task.id)}
          <li class="comment turn person">
            <div class="comment-head">{#if session.user?.avatar_url}<img class="avatar" src={session.user.avatar_url} alt="" />{/if}<b class="who">{me}</b></div>
            {#if task.asked}<p class="comment-body said">{task.asked}</p>{:else}<p class="comment-body said muted">Sent this {ab.noun}. What you wrote with it wasn’t kept in this browser.</p>{/if}
          </li>
          <li class="comment turn agent">{@render taskView(task, selected, true, name)}</li>
        {/each}
      </ol>
      <div class="convo-reply">
        <textarea aria-label="Reply to {name}" placeholder="Reply to {name}…" rows="2" maxlength="8000" bind:value={replyText} disabled={busy || underway} onkeydown={replyKey}></textarea>
        <div class="convo-reply-row">
          {#if underway}<span class="muted" role="status">Waiting for {name}…</span>{/if}
          <button class="btn small" disabled={busy || underway || !replyText.trim()} title="Send (Ctrl or ⌘ + Enter)" onclick={reply}>Send</button>
        </div>
      </div>
    {/if}
    {#if asking && ab.asksHere}
      <div class="send-row">
        <button class="btn small" disabled={busy || !instruction.trim() || underway} onclick={send}>Send {ab.noun} to agent</button>
        <details class="edit-request" bind:open={editing}><summary>Edit request</summary><textarea aria-label="Request" bind:value={instruction} maxlength="8000" rows="2"></textarea></details>
      </div>
    {/if}
  {/if}
  {#each held as c (c.id)}
    {#if tasksBy[c.id]?.length}
      <section class="relay-held" aria-labelledby="held-{c.id}">
        <h4 id="held-{c.id}">Sent earlier to {c.name}, which isn’t offered for new requests</h4>
        {#each tasksBy[c.id] as task (task.id)}<div class="comment turn agent">{@render taskView(task, c.id, false, c.name)}</div>{/each}
      </section>
    {/if}
  {/each}
  {#if error}<p role="alert">{error}</p>{:else if connectionError}<p role="alert">{connectionError}</p>{:else if relayAgents.error}<p role="alert">{relayAgents.error}</p>{/if}
</div>
<style>
  .relay-thread { display: grid; gap: 8px; padding: 8px 12px; border-top: 1px solid var(--line); background: var(--code); font-size: 12px; }
  .relay-status { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; color: var(--muted); }
  .relay-status .agent { min-width: 0; padding: 2px 6px; font-size: 12px; }
  .relay-status .agent.single { appearance: none; border-color: transparent; background: none; padding: 0; color: var(--fg); font-weight: 600; opacity: 1; }
  .dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--muted); opacity: .6; }
  .dot.live { background: var(--good); opacity: 1; }
  .dot.idle { background: var(--warn); opacity: 1; }
  .hint { display: inline; }
  .hint summary { display: inline; cursor: pointer; text-decoration: underline dotted; }
  .hint p { margin-top: 4px; }
  .send-row { display: flex; flex-wrap: wrap; align-items: start; gap: 8px; }
  .edit-request { flex: 1 1 18em; min-width: 0; color: var(--muted); }
  .edit-request summary { cursor: pointer; line-height: 26px; }
  .edit-request textarea { width: 100%; margin-top: 4px; }
  .relay-thread p { margin: 0; }
  select, textarea { color: var(--fg); font-size: 13px; }
  textarea { min-height: 0; max-width: 820px; font: 13px/1.5 ui-monospace, SFMono-Regular, monospace; }
  .relay-thread :global(.relay-body) { overflow-wrap: anywhere; }
  .relay-held { display: grid; gap: 6px; }
  .relay-held h4 { margin: 0; font-size: 12px; font-weight: 600; color: var(--muted); }
  .relay-result { display: grid; gap: 6px; }
  /* Turns are the thread's comments (.comment, .comment-head, .comment-body in diff.css), set in the panel. */
  .convo { list-style: none; margin: 0 -12px; padding: 0; border-top: 1px solid var(--line); }
  .turn { display: grid; gap: 4px; }
  .relay-held .turn { margin: 0 -12px; }
  .said { margin: 0; white-space: pre-wrap; }
  .fold-earlier { justify-self: start; display: inline-flex; gap: 6px; padding: 2px 0; border: 0; background: none; color: var(--muted); font-size: 12px; }
  .fold-earlier:hover { color: var(--fg); }
  .agent-mark { flex: none; width: 20px; height: 20px; border-radius: 6px; background: color-mix(in srgb, var(--accent) 22%, transparent); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--accent) 45%, transparent); }
  .task-state.alert { color: var(--bad); }
  .convo-reply { display: grid; gap: 6px; }
  .convo-reply textarea { width: 100%; max-width: none; padding: 6px 8px; }
  .convo-reply-row { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
</style>
